import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Gift } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../constants";

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [referrerCode, setReferrerCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Capture ?ref=CODE from URL on mount and persist for later signups
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      const upper = ref.trim().toUpperCase();
      setReferrerCode(upper);
      try { localStorage.setItem('referrer_code', upper); } catch (e) { /* noop */ }
    } else {
      try {
        const stored = localStorage.getItem('referrer_code');
        if (stored) setReferrerCode(stored);
      } catch (e) { /* noop */ }
    }
    axios.get(`${API}/promo`).then(r => setPromo(r.data)).catch(() => {});
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(name, email, password, phone, referrerCode || null);
      try { localStorage.removeItem('referrer_code'); } catch (er) { /* noop */ }
      if (result?.promo_applied?.free_vip_granted) {
        toast.success('Welcome to the Elite Crew — your free VIP is active!');
      } else {
        toast.success('Account created!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
      <div className="w-full max-w-md mx-4">
        <div className="bg-[#121212] border border-white/5 rounded-sm p-8" data-testid="register-form">
          <h1 className="font-heading text-3xl text-center mb-2">CREATE ACCOUNT</h1>
          {promo?.active && (
            <div className="text-center text-xs uppercase tracking-wider text-green-400 mb-6 flex items-center justify-center gap-2" data-testid="register-promo-perks">
              <Sparkles className="w-3 h-3" />
              Free {promo.free_vip_months}-Month VIP + {promo.discount_percent}% Off
              <Sparkles className="w-3 h-3" />
            </div>
          )}
          {!promo?.active && <div className="mb-6" />}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="register-name" required />
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="register-email" required />
            <input type="tel" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="register-phone" />
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="register-password" required />
            <div>
              <input type="text" placeholder="Referral Code (optional)" value={referrerCode} onChange={(e) => setReferrerCode(e.target.value.toUpperCase())} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4 uppercase tracking-widest" data-testid="register-referrer-code" maxLength={8} />
              {referrerCode && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1" data-testid="register-referrer-applied">
                  <Gift className="w-3 h-3" /> Referral code applied — your friend will earn $10 store credit when you make your first purchase.
                </p>
              )}
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#D62828] hover:bg-[#B91C1C] text-white py-3 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow disabled:opacity-50" data-testid="register-submit">
              {loading ? 'Creating...' : 'Create Account'}
            </button>
          </form>
          <p className="text-center text-gray-400 mt-6 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D62828] hover:underline">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
